'use client'

import type { Omit } from '@wagmi/core/internal'
import { type Config, useChainId, useConfig } from 'wagmi'

import { Evaluate } from 'viem'
import type { GetEVCTransactionErrorErrorType } from '../actions/getEVCTransactionError'
import {
  type GetEVCTransactionErrorData,
  type GetEVCTransactionErrorOptions,
  type GetEVCTransactionErrorQueryFnData,
  type GetEVCTransactionErrorQueryKey,
  getEVCTransactionErrorQueryOptions,
} from '../query'
import type { ConfigParameter, ExtendedConfig, UseQueryParameters, UseQueryReturnType } from '../types'
import { useQuery } from '../utils'

export type UseEVCTransactionErrorParameters<
  config extends Config = Config,
  selectData = GetEVCTransactionErrorData,
> = Evaluate<
  GetEVCTransactionErrorOptions &
    ConfigParameter<config> & {
      query?:
        | Evaluate<
            Omit<
              UseQueryParameters<
                GetEVCTransactionErrorQueryFnData,
                GetEVCTransactionErrorErrorType,
                selectData,
                GetEVCTransactionErrorQueryKey
              >,
              'gcTime' | 'staleTime'
            >
          >
        | undefined
      select?: (data: GetEVCTransactionErrorQueryFnData) => selectData
    }
>

export type UseEVCTransactionErrorReturnType<selectData = GetEVCTransactionErrorData> = UseQueryReturnType<
  selectData,
  GetEVCTransactionErrorErrorType
>

export function useEVCTransactionError<config extends ExtendedConfig, selectData = GetEVCTransactionErrorData>(
  parameters: UseEVCTransactionErrorParameters<config, selectData> = {},
): UseEVCTransactionErrorReturnType<selectData> {
  const { query = {}, hash, scopeKey } = parameters

  const config = useConfig(parameters)
  const chainId = useChainId({ config })

  const { queryKey, ...options } = getEVCTransactionErrorQueryOptions(config, {
    hash,
    chainId,
    scopeKey,
  })
  const enabled = Boolean(hash && (query.enabled ?? true))

  return useQuery({
    ...query,
    ...options,
    // @ts-ignore - Type constraint mismatch
    queryKey,
    enabled,
  })
}
